import { colors } from '@chegg-ui/theme'
import styled, { css } from 'styled-components'
import { fanta, PrimaryButton, OutlineButton } from '@chegg/fanta'
import { adjust } from '@chegg-wt/core/theme/theme'
import { AspiraStack } from '@chegg-wt/core/theme/fanta/fonts'
import {
  SolidSlimBorder,
  SolidBoldBorder,
  NormalRounding,
} from '@chegg-wt/core/theme/sunkist/borders'
import { ListReset, ButtonReset } from '@chegg-wt/core/theme/sunkist/resets'
import { FitToContainer } from '@chegg-wt/core/theme/sunkist/sizing'
import { values } from '@chegg-wt/core/theme/sunkist/spacing'
import { BodyLarge, InputLarge, Ellipsize } from '@chegg-wt/core/theme/sunkist/typography'

export const smallFrameLayout = ({ theme }) => theme.breakpoints[1]

export const Heading = styled.h1`
  ${AspiraStack};
  font-size: ${fanta.font.size.xxl};
  font-weight: ${fanta.font.weight.bold};
  color: ${colors.dark};
  margin-top: 0px;

  @media (max-width: ${(props) => adjust(smallFrameLayout(props), -1)}) {
    text-align: center;
  }
`

export const ConfirmButton = styled(PrimaryButton).attrs({
  isHoverAnimated: true,
  size: 'medium',
})`
  background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
  border-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
  width: 189px;
  &:hover {
    border-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor23};
    background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor23};
    color: #fff;
  }
  &:disabled {
    color: ${colors.grey};
    background-color: ${colors.greyXlight};
    border-color: ${colors.greyXlight};
  }
  @media (max-width: ${(props) => adjust(smallFrameLayout(props), -1)}) {
    width: 100%;
  }
`

export const CancelButton = styled(OutlineButton).attrs({
  isHoverAnimated: true,
  size: 'medium',
})`
  color: ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
  border-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
  width: 189px;
  &:hover {
    border-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor23};
    background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor23};
    color: #fff;
  }
  @media (max-width: ${(props) => adjust(smallFrameLayout(props), -1)}) {
    width: 100%;
  }
`

export const ItemAction = styled.button`
  ${ButtonReset};
  ${FitToContainer};
  ${BodyLarge};
  ${Ellipsize};
  padding: ${values.small};

  &[data-selected] {
    background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor12};
  }

  &:hover {
    background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor12};
  }

  &:focus {
    background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor12};
  }
`

export const Item = styled.li``

/**
 * [1]: Value specified in designs is not present in forInterface list
 * [2]: Arbitrary value
 *
 * TODO: Having a border color the same as the text color is not that uncommon, bring this up with Chegg design team to avoid this kind of distinction in the future.
 */
export const Items = styled.ul`
  ${ListReset};
  ${SolidSlimBorder};
  ${NormalRounding};
  background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor11};
  border-color: ${({ theme }) => theme.Colors.forFonts.fColor02}; /* [1] */
  max-height: 250px; /* [2] */
  overflow-x: hidden;
  overflow-y: auto;
`

/**
 * TODO: Figure out why [1] is necessary
 * [1]: This fixes a number of alignment issues
 */
export const Icon = styled.span<{
  icon: string
  iconSize?: string
  iconWidth?: string
  iconHeight?: string
}>`
  display: inline-block;

  background-image: url('${({ icon }) => icon}');
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;

  ${({ iconSize, iconWidth, iconHeight }) =>
    /* eslint-disable no-nested-ternary */
    iconSize
      ? css`
          width: ${iconSize};
          height: ${iconSize};
        `
      : iconWidth && iconHeight
      ? css`
          width: ${iconWidth};
          height: ${iconHeight};
        `
      : css`
          width: 1em;
          height: 1em;
        `}

  /* [1] */
    &::before {
    content: '';
    display: inline-block;
  }
`

export const Label = styled.span`
  ${Ellipsize};
  ${InputLarge};
  color: ${({ theme }) => theme.Colors.forInterface.interfaceColor17};
`

/**
 * [1]: Layout concerns
 */
export const Face = styled.button`
  ${ButtonReset};
  ${FitToContainer};
  ${SolidBoldBorder};
  background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor11};
  border-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor13};
  padding: ${values.xsmall};

  /* [1] */
  display: flex;
  align-items: center;

  & > * + * {
    margin-left: ${values.small};
  }

  & > * {
    flex: 0 0 auto;
  }

  & > ${Label} {
    min-width: 0;
    flex: 1 1 auto;
  }
`

export const Layout = styled.div`
  position: relative;

  & ${Items} {
    position: absolute;
    width: 100%;
    z-index: 1;
  }
`
